import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  FileSearch,
  MessageSquareQuote,
  Wand2,
  LayoutGrid,
  Telescope,
  ArrowUpRight,
} from 'lucide-react';
import { fadeUp, displaySection, kicker, sectionWrap, sectionInner } from './shared';

/**
 * FeatureShowcase — 5 features beyond profile optimization, each paired with
 * a REAL product screenshot captured from the live app under
 * /public/landing-shots. We frame each shot in a soft device card.
 */
const FEATURES = [
  {
    id: 'researched',
    Icon: FileSearch,
    kicker: '01 · Researched posts',
    title: (
      <>
        Researched posts<br />
        <span className="text-primary">with real research built in.</span>
      </>
    ),
    copy:
      'Pick a topic and how often you want to post. We surface real industry signals and the latest data points, then hand you a draft. No more guessing what to write on Monday.',
    bullets: [
      '6 posts per month combined (Researched + PoV + Prompt)',
      'Real-time research with verifiable sources',
      'We figure out your post\u2019s angle for you',
    ],
    img: '/landing-shots/screen_researched.jpeg',
    alt: 'Researched Posts panel inside Blayzrr',
    flip: false,
  },
  {
    id: 'pov',
    Icon: MessageSquareQuote,
    kicker: '02 · PoV posts',
    title: (
      <>
        Not sure what to comment on a trending post?<br />
        <span className="text-primary">Get voice-matched points of view.</span>
      </>
    ),
    copy:
      'Drop a trending headline or any article URL. We write a voice-matched point of view, hold a clear stance, and back it with sources you can verify.',
    bullets: [
      'Get commenting ideas on trending posts',
      'Voice-matched PoV from any article',
      'Conversational \u2192 contrarian tone control',
    ],
    img: '/landing-shots/screen_pov.jpeg',
    alt: 'PoV Posts tab inside Blayzrr',
    flip: true,
  },
  {
    id: 'prompt',
    Icon: Wand2,
    kicker: '03 · Prompt posts',
    title: (
      <>
        Type a brief.<br />
        <span className="text-primary">Get four drafts that sound like you.</span>
      </>
    ),
    copy:
      'Paste a few notes, pick how many posts and how long. Choose a country context, tone rotation, and connect to your day-job expertise. The drafts come back researched and editable down to the comma.',
    bullets: [
      'Per-post word count and tone rotation',
      'Country / region context (US, UK, IN, AE\u2026)',
      'Pulls in your functional expertise + ICP',
    ],
    img: '/landing-shots/screen_prompt.jpeg',
    alt: 'Prompt Posts editor inside Blayzrr',
    flip: false,
  },
  {
    id: 'carousel',
    Icon: LayoutGrid,
    kicker: '04 · Carousel designer',
    title: (
      <>
        Premium carousels,<br />
        <span className="text-primary">no Canva tabs required.</span>
      </>
    ),
    copy:
      'Paste any brief and pick a designed template. We turn it into a polished LinkedIn-ready PDF you can post as-is.',
    bullets: [
      'Multiple designer templates ready to use',
      'Slide count picked automatically by length',
      'Downloads as a polished LinkedIn-ready PDF',
    ],
    img: '/landing-shots/screen_designer.jpeg',
    alt: 'Carousel Designer with premium templates',
    flip: true,
  },
  {
    id: 'inspiration',
    Icon: Telescope,
    kicker: '05 · Inspiration & benchmarks*',
    title: (
      <>
        Benchmark against the leaders.*<br />
        <span className="text-primary">Ride the topics that are trending now.</span>
      </>
    ),
    copy:
      'Pin up to 10 profiles from your industry and see their posting style, plus get inspired by 20 trending topics from your function or industry. *Peer benchmarking is part of the Enterprise Plan.',
    bullets: [
      'Pin up to 10 profiles from your industry (Enterprise*)',
      '20 trending topics from your function / industry',
      'Filter by country + engagement level',
    ],
    img: '/landing-shots/screen_inspiration.jpeg',
    alt: 'Inspiration tab benchmarking leaders',
    flip: false,
  },
];

export const FeatureShowcase = () => (
  <section className={sectionWrap} id="product" aria-label="Product features">
    <div className={sectionInner}>
      <motion.div {...fadeUp} className="mb-16 max-w-3xl">
        <p className={kicker}>What you actually get</p>
        <h2 className={`${displaySection} mt-4 text-4xl sm:text-5xl lg:text-6xl`}>
          A content engine —<br />
          <span className="text-primary">not another writing tool.</span>
        </h2>
        <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
          Five tools that compound: researched posts, sharp PoVs, prompt drafts, premium carousels, and an inspiration feed that benchmarks you against the leaders in your niche.
        </p>
      </motion.div>

      <div className="space-y-20 sm:space-y-28">
        {FEATURES.map((f) => (
          <FeatureRow key={f.id} feature={f} />
        ))}
      </div>
    </div>
  </section>
);

const FeatureRow = ({ feature }) => {
  const { Icon, kicker: kickerText, title, copy, bullets, img, alt, flip } = feature;
  return (
    <motion.div
      {...fadeUp}
      className={`grid items-center gap-10 lg:gap-16 lg:grid-cols-2 ${flip ? 'lg:[&>div:first-child]:order-2' : ''}`}
    >
      <div>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[hsl(var(--ep-accent-soft,212_100%_96%))] grid place-items-center text-primary border border-primary/15">
            <Icon size={18} aria-hidden="true" />
          </div>
          <p className="text-[11px] font-mono-edge tracking-[0.22em] uppercase text-primary">{kickerText}</p>
        </div>
        <h3 className="mt-5 font-display font-semibold text-3xl sm:text-4xl lg:text-[44px] tracking-[-0.04em] leading-[1.05]">
          {title}
        </h3>
        <p className="mt-4 text-base text-muted-foreground leading-relaxed max-w-md">{copy}</p>
        <ul className="mt-6 space-y-2.5">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-2 text-sm">
              <span className="mt-1.5 w-1 h-1 rounded-full bg-primary shrink-0" aria-hidden="true" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <ProductShot src={img} alt={alt} />
      </div>
    </motion.div>
  );
};

/** ProductShot — lightweight macOS-style frame around a real captured screenshot. */
const ProductShot = ({ src, alt }) => (
  <Card className="relative rounded-3xl border border-border/70 bg-card/90 backdrop-blur overflow-hidden shadow-[0_30px_80px_-50px_hsl(220_40%_2%/0.4)]">
    <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/60 bg-card/95">
      <div className="flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" aria-hidden="true" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" aria-hidden="true" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" aria-hidden="true" />
      </div>
      <Badge variant="secondary" className="rounded-full text-[10px] font-mono-edge tracking-[0.2em] uppercase">
        blayzrr.com
      </Badge>
      <span className="w-10" />
    </div>
    <div className="relative">
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="w-full h-auto block"
        width="1440"
        height="900"
      />
      <a
        href="/signup"
        className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-foreground/85 text-background text-[11px] font-medium px-3 py-1.5 backdrop-blur-sm hover:bg-foreground transition-colors"
        aria-label="Try this feature inside Blayzrr"
      >
        Try it <ArrowUpRight size={11} aria-hidden="true" />
      </a>
    </div>
  </Card>
);
